import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, MapPin } from "lucide-react";

export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer
      className="mt-24 text-white"
      style={{ backgroundColor: "var(--nibro-sage-dark)" }}
      data-testid="site-footer"
    >
      <div className="mx-auto max-w-[1400px] px-5 py-14 lg:px-10">
        {/* International SEO industries mega-section (English, kept for SEO) */}
        <div className="mb-12 border-b border-white/15 pb-10">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/60">
                {t("footer.industries_kicker")}
              </div>
              <h4 className="mt-2 font-display text-xl font-bold text-white">
                {t("footer.industries_title")}
              </h4>
            </div>
            <ul className="space-y-2 text-sm md:col-span-2 md:columns-2">
              <FooterLink to="/landing/food-grade-pipe-clamps">Food &amp; Beverage</FooterLink>
              <FooterLink to="/landing/dairy-pipe-supports">Dairy</FooterLink>
              <FooterLink to="/landing/brewery-pipe-clamps">Brewery</FooterLink>
              <FooterLink to="/landing/pharmaceutical-pipe-supports">Pharmaceutical</FooterLink>
              <FooterLink to="/landing/semiconductor-pipe-clamps">Semiconductor</FooterLink>
              <FooterLink to="/landing/water-treatment-pipe-clamps">Water Treatment</FooterLink>
              <FooterLink to="/landing/hydrogen-pipe-clamps">Hydrogen / H₂</FooterLink>
              <FooterLink to="/landing/marine-offshore-pipe-clamps">Marine &amp; Offshore</FooterLink>
              <FooterLink to="/landing/chemical-plant-pipe-clamps">Chemical Plants</FooterLink>
              <FooterLink to="/landing/hvac-pipe-clamps">HVAC</FooterLink>
              <FooterLink to="/landing/custom-pipe-clamps">Custom / Made to Drawing</FooterLink>
              <FooterLink to="/landing/oem-pipe-supports">OEM Supply</FooterLink>
              <FooterLink to="/landing/european-manufacturer">European Manufacturer</FooterLink>
              <FooterLink to="/landing/why-perfectly-straight-matters">Why Perfectly Straight</FooterLink>
            </ul>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/60">
                {t("footer.standards_kicker")}
              </div>
              <h4 className="mt-2 font-display text-xl font-bold text-white">
                {t("footer.standards_title")}
              </h4>
            </div>
            <ul className="space-y-2 text-sm md:col-span-2 md:columns-2">
              <FooterLink to="/landing/en-10357-pipe-clamps">EN 10357 Clamps</FooterLink>
              <FooterLink to="/landing/din-11852-clamps">DIN 11852 Clamps</FooterLink>
              <FooterLink to="/landing/asme-bpe-clamps">ASME BPE Clamps</FooterLink>
              <FooterLink to="/landing/atex-zone-pipe-clamps">ATEX Zone Clamps</FooterLink>
              <FooterLink to="/landing/electropolished-pipe-clamps">Electropolished Ra ≤ 0.4 µm</FooterLink>
              <FooterLink to="/landing/duplex-1-4462-pipe-clamps">Duplex 1.4462</FooterLink>
              <FooterLink to="/landing/super-duplex-pipe-clamps">Super Duplex 1.4410</FooterLink>
              <FooterLink to="/landing/cryogenic-lng-pipe-clamps">Cryogenic / LNG -196 °C</FooterLink>
              <FooterLink to="/landing/high-vibration-pipe-clamps">High-Vibration Pumps</FooterLink>
              <FooterLink to="/landing/high-temperature-steam-pipe-clamps">High-Temperature Steam &gt;200 °C</FooterLink>
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-10 md:grid-cols-4">
          <div>
            <h4 className="font-display text-base font-bold uppercase tracking-wide text-white">
              {t("footer.products")}
            </h4>
            <ul className="mt-5 space-y-2.5 text-sm">
              <FooterLink to="/products?category=Pipe Clamps">{t("header.clamps")}</FooterLink>
              <FooterLink to="/products?category=Heavy Duty Clamps">DIN 3567 Heavy</FooterLink>
              <FooterLink to="/products?category=Pipe Shoes">{t("header.pipe_shoes")}</FooterLink>
              <FooterLink to="/products?category=U-Bolts">U-Bolts DIN 3570</FooterLink>
              <FooterLink to="/products?category=Rubber Lined Clamps">Rubber Lined</FooterLink>
              <FooterLink to="/configurator">{t("header.custom")}</FooterLink>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-base font-bold uppercase tracking-wide text-white">
              {t("footer.info")}
            </h4>
            <ul className="mt-5 space-y-2.5 text-sm">
              <FooterLink to="/industries">{t("footer.industries_link")}</FooterLink>
              <FooterLink to="/blog">{t("footer.knowledge_base_link")}</FooterLink>
              <FooterLink to="/conversion">{t("footer.conversion_link")}</FooterLink>
              <FooterLink to="/pipe-scan">{t("footer.pipe_scan_link")}</FooterLink>
              <FooterLink to="/products">{t("footer.products_link")}</FooterLink>
              <FooterLink to="/contact">{t("footer.contact_rfq_link")}</FooterLink>
            </ul>
          </div>

          <div>
            <img
              src="/aramfix-logo-header.png"
              alt="ARAMFIX — your global fastener supplier"
              className="h-16 w-auto"
            />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/85">
              {t("footer.company_blurb")}
            </p>
            <div className="mt-5 flex gap-2">
              <Badge>DIN 3567</Badge>
              <Badge>DIN 3570</Badge>
              <Badge>EN 10088</Badge>
            </div>
          </div>

          <div>
            <h4 className="font-display text-base font-bold uppercase tracking-wide text-white">
              {t("footer.contact")}
            </h4>
            <ul className="mt-5 space-y-3 text-sm">
              <li className="flex gap-2">
                <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                <div className="leading-relaxed text-white/90">
                  Nibro BV<br />
                  Nijverheidsweg 13a<br />
                  5061KK Oisterwijk<br />
                  Nederland
                </div>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={14} />
                <a href="mailto:info@aramfix.com" className="text-white/90 hover:text-white">
                  info@aramfix.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t-2 border-aramfix-red/40 pt-5 text-xs text-white/80 md:flex-row md:items-center">
          <div>
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </div>
          <div className="flex items-center gap-4 font-mono uppercase tracking-[0.18em]">
            <a
              href="/sitemap.xml"
              className="text-white/70 hover:text-white"
              data-testid="footer-sitemap-link"
            >
              {t("footer.sitemap")}
            </a>
            <span>{t("footer.made_in")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ to, children }) {
  return (
    <li>
      <Link to={to} className="text-white/85 transition hover:text-white">
        {children}
      </Link>
    </li>
  );
}

function Badge({ children }) {
  return (
    <span className="rounded border border-white/30 bg-white/10 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-white">
      {children}
    </span>
  );
}
